import { useMemo, useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShortVideoCard from "@/components/feature/ShortVideoCard";
import EmptyState from "@/components/feature/EmptyState";
import { shortVideos } from "@/mocks/shortVideos";
import { shortSorts, shortTags } from "@/mocks/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const STEP = 8;

export default function ShortVideos() {
  usePageMeta(
    "午夜视频 - 精彩午夜短视频浏览",
    "午夜视频短视频分类页，收录剧情、生活、搞笑与剪辑类精选短视频，支持标签筛选与最新、最热排序，随时发现深夜片段。",
    "午夜短视频,深夜短视频,精选短片,短视频推荐,午夜视频短视频",
  );

  const [tag, setTag] = useState("全部");
  const [sort, setSort] = useState("newest");
  const [visibleCount, setVisibleCount] = useState(STEP);

  const filtered = useMemo(() => {
    const list = shortVideos.filter((video) => tag === "全部" || video.tag === tag);
    return [...list].sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      return a.uploadDate < b.uploadDate ? 1 : -1;
    });
  }, [tag, sort]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <PageLayout>
      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: "短视频分类" }]} />

        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="flex items-center gap-2 font-label text-xs tracking-[0.3em] text-accent-500">
              <span className="inline-block h-px w-6 bg-accent-500/70" />
              SHORT VIDEO
            </p>
            <h1 className="mt-3 font-heading text-2xl font-bold tracking-wide text-foreground-50 md:text-3xl">
              精彩午夜短视频
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-400">
              从三分钟剧情短片到深夜厨房纪录，按标签快速切换口味。切换标签或排序后列表即时刷新，新的内容会追加在下方。
            </p>
          </div>
          <div className="flex items-center gap-4 rounded-lg border border-background-800 bg-background-900/60 px-4 py-3">
            <div>
              <p className="font-label text-xl text-accent-400">{shortVideos.length}</p>
              <p className="mt-0.5 text-[11px] text-foreground-500">精选短片</p>
            </div>
            <span className="h-8 w-px bg-background-700" />
            <div>
              <p className="font-label text-xl text-secondary-200">4</p>
              <p className="mt-0.5 text-[11px] text-foreground-500">内容标签</p>
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-col gap-4 rounded-lg border border-background-800 bg-background-900/70 p-4 md:flex-row md:items-center md:justify-between md:p-5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs text-foreground-500">标签</span>
            {shortTags.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setTag(item);
                  setVisibleCount(STEP);
                }}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  tag === item
                    ? "bg-accent-500 font-medium text-background-950"
                    : "bg-background-800/80 text-foreground-300 hover:bg-background-700 hover:text-foreground-50"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-full border border-background-700 bg-background-950 p-1">
            {shortSorts.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => {
                  setSort(item.value);
                  setVisibleCount(STEP);
                }}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                  sort === item.value
                    ? "bg-primary-600 text-foreground-50"
                    : "text-foreground-400 hover:text-foreground-100"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-4 text-xs text-foreground-500">
          共 <span className="text-accent-400">{filtered.length}</span> 条内容 · 已展示{" "}
          {Math.min(visibleCount, filtered.length)} 条
        </p>

        <div className="mt-5">
          {filtered.length === 0 ? (
            <EmptyState
              icon="ri-video-line"
              title="这个标签下还没有内容"
              description="换一个标签看看，或者回到全部短视频，那里有更多深夜片段在等你。"
              actionLabel="查看全部短视频"
              actionTo="/short-videos"
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
                {visible.map((video, index) => (
                  <div
                    key={video.id}
                    className="animate-rise"
                    style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
                  >
                    <ShortVideoCard
                      id={video.id}
                      title={video.title}
                      tag={video.tag}
                      duration={video.duration}
                      views={video.views}
                      cover={video.cover}
                      uploadDate={video.uploadDate}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((count) => count + 4)}
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3.5 text-sm font-medium text-background-950 transition-colors hover:bg-accent-400 sm:w-auto"
                  >
                    <i className="ri-arrow-down-line text-base" />
                    加载更多短视频
                  </button>
                ) : (
                  <p className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-checkbox-circle-line text-sm text-secondary-400" />
                    已经看到本次筛选的全部内容了
                  </p>
                )}
                {hasMore && (
                  <p className="text-xs text-foreground-600">
                    还有 {filtered.length - visibleCount} 条内容等待加载
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}